import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const FloatingNavToggle = ({
  visible,
  onToggle,
  offset = 0
}) => {
  const [expanded, setExpanded] = useState(false);
  const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
  
  // Keyboard shortcut: Ctrl+Shift+H to toggle
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'h') {
        e.preventDefault();
        onToggle?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onToggle]);

  const handleMouseEnter = () => setExpanded(true);
  const handleMouseLeave = () => setExpanded(false);
  
  const containerStyle = {
    position: 'fixed',
    top: `calc(${offset}px + env(safe-area-inset-top, 0px))`,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  };

  const toggleStyle = {
    width: expanded ? '40px' : '100px',
    height: '18px',
    borderRadius: '0 0 20px 20px',
    backgroundColor: isDark ? 'rgba(33,37,41,0.9)' : 'rgba(248,249,250,0.9)',
    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)',
    borderTop: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    transition: 'width 0.3s ease',
  };

  // Make the panel absolute so it doesn't push the toggle down
  const panelStyle = {
    position: 'absolute',
    top: 0,
    left: '50%',
    transform: expanded ? 'translate(-50%, 0)' : 'translate(-50%, calc(-100% - 8px))',
    opacity: expanded ? 1 : 0,
    transition: 'transform 0.25s ease, opacity 0.2s ease',
    backgroundColor: isDark ? 'rgba(33,37,41,0.98)' : 'rgba(248,249,250,0.98)',
    border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)',
    borderRadius: '12px',
    padding: '12px 16px', // was '12px 16px 30px 16px' (extra bottom space for caret)
    boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    pointerEvents: expanded ? 'auto' : 'none',
    width: 'max-content',
  };

  const caretStyle = {
    position: 'absolute',
    bottom: 0,
    width: '40px',
    height: '18px',
    borderRadius: '0 0 20px 20px',
    backgroundColor: 'inherit',
    border: 'inherit',
    borderTop: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  // SVG icons instead of relying on icon fonts
  const eyeIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
    </svg>
  );
  
  const eyeOffIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
      <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z"/>
      <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z"/>
      <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z"/>
    </svg>
  );

  return createPortal(
    <div 
      style={containerStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="floating-navbar-toggle user-select-none"
      role="group"
      aria-label="Navbar visibility controls"
    >
      {/* Panel with controls */}
      <div style={panelStyle}>
        <div className="d-flex align-items-center gap-3 flex-nowrap">  {/* prevent wrapping */}
          <span
            style={{ color: isDark ? '#e9ecef' : '#495057' }}
            className="small fw-semibold"
          >
            {visible ? 'Hide header' : 'Show header'}
          </span>

          <button
            type="button"
            onClick={onToggle}
            className="btn btn-sm rounded-pill"
            style={{
              backgroundColor: isDark ? 'rgba(73,80,87,0.6)' : 'rgba(233,236,239,0.9)',
              color: isDark ? '#e9ecef' : '#495057',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.06)',
              whiteSpace: 'nowrap',            // keep button label + icon on one line
            }}
            aria-label={visible ? "Hide header" : "Show header"}
            aria-pressed={!visible}
          >
            {visible ? (
              <span>Hide <span className="ms-1">{eyeOffIcon}</span></span>
            ) : (
              <span>Show <span className="ms-1">{eyeIcon}</span></span>
            )}
          </button>
        </div>
        

      </div>
      
      {/* Toggle button when collapsed */}
      {!expanded && (
        <div style={toggleStyle} onClick={() => setExpanded(true)}>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="14" 
            height="14" 
            fill="currentColor" 
            viewBox="0 0 16 16"
            style={{
              color: isDark ? '#e9ecef' : '#495057'
            }}
          >
            <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
          </svg>
        </div>
      )}
    </div>,
    document.body
  );
};

export default FloatingNavToggle;